/**
 * 车辆诊断表格数据(在此组件区分展示adas设备)
 */
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Button, Tabs } from 'antd';
import CommonTable from '@/components/commonTable';
import { carAdasRequest, carAdasRequestPath } from '@/service/carDiagnosis';
import { useTable } from '@/utils/useTable';
import dayjs from 'dayjs';
import pageStyles from '@/styles/pageContent.module.scss';
import ExportButton from '@/components/exportButton';
import JtDeviceErrorTable from "@/pages/carDiagnose/components/carDiagnosisTable/jtDeviceErrorTable";
import JtDeviceVersionChangeTable from "@/pages/carDiagnose/components/carDiagnosisTable/jtDeviceVersionChangeTable";
import { useDispatch } from 'react-redux';
import JtDeviceParamsChangeTable from '../carDiagnosisTable/jtDeviceParamsChangeTable';
import { DeviceTypeEnum } from '@/constants/deviceManage';

interface CarDiagnosisTableProps {
  diagnosisSearchParams: any;
  isTly40?: boolean;//是否是TLY4.0
  plateNumber?: string;
  carId?: string,
  deviceType?: DeviceTypeEnum
}

const CarAdasTable = (props: CarDiagnosisTableProps) => {
  const { diagnosisSearchParams, isTly40, plateNumber, carId = '', deviceType = '' } = props;
  const [searchParams, setSearchParams] = useState(diagnosisSearchParams);
  const dispatch = useDispatch();
  // 监听搜索参数的变化重新获取数据
  useEffect(() => {
    setSearchParams(diagnosisSearchParams);
  }, [diagnosisSearchParams]);


  const { page, limit, loading, data, totalCount, onPageChange } = useTable({
    requestFunc: carAdasRequest,
    initRequest: true,
    initPageSize: 40,
    searchParams,
  });

  // 获取导出的参数
  const getExportParams = async () => {
    if (searchParams) {
      return {
        ...searchParams,
        export: true,
      }
    }
  }

  // 点击表格日期跳转到驾驶行为分析页面
  const onDateClick = (plateNumber: string, date: string) => {
    dispatch({
      type: 'addTab',
      payload: {
        tab: '驾驶行为分析',
        key: '/car-admin/drive-analysis',
        params: { plateNumber, date }
      }
    })
  }

  // 表格列显示
  const columns: any = [
    {
      title: '日期',
      dataIndex: 'day',
      width: 70,
      render: (value: string, item: any) => (
        <div className={pageStyles.tableOperateColumn}>
          <Button
            type="link"
            onClick={() => onDateClick(item.plateNumber, dayjs(value).format('YYYY-MM-DD'))}
          >
            {dayjs(value).format('MM-DD')}
          </Button>
        </div>
      ),
      fixed: 'left' as 'left'
    },
    {
      title: '运行时长(分)',
      dataIndex: 'minute',
      width: 120,
      render: (value: number) => value === null ? '--' : value
    },
    {
      title: '运行里程(km)',
      dataIndex: 'diffMileage',
      width: 120,
    },
    {
      title: '修正有效运行分钟数',
      dataIndex: 'minuteHuman',
      width: 150,
    },
    {
      title: '修正里程数',
      dataIndex: 'diffMileageHuman',
      width: 120,
    },
    // 新增字段，俊驰和极目设备均显示
    {
      title: '车速为0',
      dataIndex: 've0Count',
      width: 120,
      deviceTypeFlag: true,
      deviceType: [DeviceTypeEnum.junchi1, DeviceTypeEnum.jimu],
    },
    {
      title: 'GPS速度为零',
      dataIndex: 'ge0Count',
      width: 120,
      deviceTypeFlag: true,
      deviceType: [DeviceTypeEnum.junchi1, DeviceTypeEnum.jimu],
    },
    {
      title: '车速大于120',
      dataIndex: 'vm120Count',
      width: 120,
      deviceTypeFlag: true,
      deviceType: [DeviceTypeEnum.junchi1, DeviceTypeEnum.jimu],
    },
    {
      title: '车身信号次数',
      dataIndex: 'signCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: [DeviceTypeEnum.junchi1, DeviceTypeEnum.jimu],
    },
    {
      title: '刹车信号次数',
      dataIndex: 'breakCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: [DeviceTypeEnum.junchi1, DeviceTypeEnum.jimu],
    },
    {
      title: '左转次数',
      dataIndex: 'leftCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: [DeviceTypeEnum.junchi1, DeviceTypeEnum.jimu],
    },
    {
      title: '右转次数',
      dataIndex: 'rightCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: [DeviceTypeEnum.junchi1, DeviceTypeEnum.jimu],
    },
    {
      title: '前向碰撞报警次数',
      dataIndex: 'fcwCount',
      width: 145,
    },
    {
      title: '车道偏离报警数',
      dataIndex: 'ldwCount',
      width: 145,
    },
    {
      title: '车距过近报警数',
      dataIndex: 'hmwCount',
      width: 120,
    },
    {
      title: '行人碰撞报警数',
      dataIndex: 'pcwCount',
      width: 120,
    },
    {
      title: '前向制动报警数',
      dataIndex: 'aebCount',
      width: 160,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.junchi1,
    },
    {
      title: '疲劳驾驶报警数',
      dataIndex: 'fdwCount',
      width: 120,
    },
    {
      title: '接打电话报警数',
      dataIndex: 'mpwCount',
      width: 120,
    },
    {
      title: '抽烟报警数',
      dataIndex: 'sdwCount',
      width: 120,
    },
    {
      title: '分神驾驶报警数',
      dataIndex: 'ddwCount',
      width: 120,
    },
    {
      title: '闭眼报警数',
      dataIndex: 'bywCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.junchi1,
    },
    {
      title: '打哈欠报警数',
      dataIndex: 'hqwCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.junchi1,
    },
    {
      title: '驾驶员异常报警数',
      dataIndex: 'dawCount',
      width: 120,
    },
    {
      title: '相机遮挡报警数',
      dataIndex: 'cowCount',
      width: 120,
    },
    {
      title: '未系安全带报警数',
      dataIndex: 'nwwCount',
      width: 120,
    },
    {
      title: '玩手机报警数',
      dataIndex: 'ppwCount',
      width: 120,
    },
    {
      title: '盲区报警数',
      dataIndex: 'bsdCount',
      width: 120,
    },
    {
      title: 'gnss模块故障数',
      dataIndex: 'gnssErrorCount',
      width: 120,
    },
    {
      title: 'gnss天线未接或被剪断数',
      dataIndex: 'gnssConnectionFailureCount',
      width: 160,
    },
    {
      title: 'gnss天线短路数',
      dataIndex: 'gnssShortCircuit',
      width: 120,
    },
    {
      title: '终端主电源欠压',
      dataIndex: 'powerUndervoltageCount',
      width: 120,
    },
    {
      title: '终端主电源掉电',
      dataIndex: 'powerDownCount',
      width: 120,
    },
    {
      title: '终端LCD或显示器故障数',
      dataIndex: 'lcdErrorCount',
      width: 160,
    },
    {
      title: 'TTS模块故障数',
      dataIndex: 'ttsErrorCount',
      width: 120,
    },
    {
      title: '摄像头故障数',
      dataIndex: 'cameraErrorCount',
      width: 120,
    },
    {
      title: '道路运输证IC卡模块故障数',
      dataIndex: 'icErrorCount',
      width: 180,
    },
    {
      title: '车辆VSS故障数',
      dataIndex: 'vehicleVssErrorCount',
      width: 120,
    },
    {
      title: '车辆油量异常数',
      dataIndex: 'vehicleOilErrorCount',
      width: 120,
    },
    {
      title: '车辆被盗数',
      dataIndex: 'vehicleTheftCount',
      width: 120,
    },
    {
      title: '车辆非法点火数',
      dataIndex: 'vehicleLaunchIllegallyCount',
      width: 140,
    },
    {
      title: '车辆非法位移数',
      dataIndex: 'vehicleMoveIllegallyCount',
      width: 140,
    },
    {
      title: '通道1相机信号丢失',
      dataIndex: 'cameraSignalLoss1Count',
      width: 140,
    },
    {
      title: '通道2相机信号丢失',
      dataIndex: 'cameraSignalLoss2Count',
      width: 140,
    },
    {
      title: '通道3相机信号丢失',
      dataIndex: 'cameraSignalLoss3Count',
      width: 140,
    },
    {
      title: '通道4相机信号丢失',
      dataIndex: 'cameraSignalLoss4Count',
      width: 140,
    },
    {
      title: '通道5相机信号丢失',
      dataIndex: 'cameraSignalLoss5Count',
      width: 140,
    },
    {
      title: '通道1相机遮挡',
      dataIndex: 'cameraMask1Count',
      width: 120,
    },
    {
      title: '通道2相机遮挡',
      dataIndex: 'cameraMask2Count',
      width: 120,
    },
    {
      title: '通道3相机遮挡',
      dataIndex: 'cameraMask3Count',
      width: 120,
    },
    {
      title: '通道4相机遮挡',
      dataIndex: 'cameraMask4Count',
      width: 120,
    },
    {
      title: '通道5相机遮挡',
      dataIndex: 'cameraMask5Count',
      width: 120,
    },
    {
      title: '存储单元故障',
      dataIndex: 'memoryFailureCount',
      width: 120,
    },
    {
      title: 'CANBUS接线',
      dataIndex: 'canBusWiringErrorCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.junchi1,
    },
    {
      title: 'CANBUS车速解析',
      dataIndex: 'canBusSpeedAnalysisErrorCount',
      width: 140,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.junchi1,
    },
    {
      title: '定位速度与总线车速故障',
      dataIndex: 'carSpeedErrorCount',
      width: 160,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.junchi1,
    },
    {
      title: '执行机构故障',
      dataIndex: 'executingAgencyErrorCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.junchi1,
    },
    {
      title: 'SD卡故障',
      dataIndex: 'sdCardErrorCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.junchi1,
    },
    {
      title: 'SIM卡故障',
      dataIndex: 'simCardErrorCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.junchi1,
    },
    {
      title: '惯导模块故障',
      dataIndex: 'inertialGuidanceModuleErrorCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.junchi1,
    },
    {
      title: '定位模块故障',
      dataIndex: 'positioningModuleErrorCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.junchi1,
    },
    {
      title: '前盲雷达故障',
      dataIndex: 'frontBlindRadarErrorCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.junchi1,
    },
    {
      title: '右盲雷达故障',
      dataIndex: 'rightBlindRadarErrorCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.junchi1,
    },
    {
      title: '左盲雷达故障',
      dataIndex: 'leftBlindRadaErrorCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.junchi1,
    },
    {
      title: '后盲雷达故障',
      dataIndex: 'rearBlindRadarErrorCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.junchi1,
    },
    {
      title: '前向雷达故障',
      dataIndex: 'forwardFacingRadarErrorCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.junchi1,
    },
    {
      title: '前盲摄像头故障',
      dataIndex: 'frontBlindCameraErrorCount',
      width: 140,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.junchi1,
    },
    {
      title: '右盲摄像头故障',
      dataIndex: 'rightBlindCameraErrorCount',
      width: 140,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.junchi1,
    },
    {
      title: '左盲摄像头故障',
      dataIndex: 'leftBlindCameraErrorCount',
      width: 140,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.junchi1,
    },
    {
      title: '后盲摄像头故障',
      dataIndex: 'rearBlindCameraErrorCount',
      width: 140,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.junchi1,
    },
    {
      title: '前向摄像头故障',
      dataIndex: 'forwardFacingCameraErrorCount',
      width: 140,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.junchi1,
    },
    {
      title: '盲区制动主动屏蔽(人为)',
      dataIndex: 'blindBrakeManShieldErrorCount',
      width: 180,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.junchi1,
    },
    {
      title: '前向制动主动屏蔽(人为)',
      dataIndex: 'forwardBrakeManShieldErrorCount',
      width: 180,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.junchi1,
    },
    {
      title: '盲区制动平台屏蔽',
      dataIndex: 'blindBrakePlatformShieldErrorCount',
      width: 140,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.junchi1,
    },
    {
      title: '前向制动平台屏蔽',
      dataIndex: 'forwardBrakePlatformShieldErrorCount',
      width: 140,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.junchi1,
    },
    {
      title: '系统自检',
      dataIndex: 'systemSelfTestErrorCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.junchi1,
    },
    {
      title: '车速获取异常',
      dataIndex: 'vehicleSpeedErrCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.jimu,
    },
    {
      title: '刹车灯故障',
      dataIndex: 'signalBreakErrCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.jimu,
    },
    {
      title: '左转灯故障',
      dataIndex: 'signalLeftErrCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.jimu,
    },
    {
      title: '右转灯故障',
      dataIndex: 'signalRightErrCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.jimu,
    },
    {
      title: 'CAN0总线故障',
      dataIndex: 'can0ErrCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.jimu,
    },
    {
      title: 'CAN1总线故障',
      dataIndex: 'can1ErrCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.jimu,
    },
    {
      title: 'ADAS相机故障',
      dataIndex: 'aebCameraErrCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.jimu,
    },
    {
      title: 'DMS相机故障',
      dataIndex: 'dmsCameraErrCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.jimu,
    },
    {
      title: 'BSD右前相机故障',
      dataIndex: 'bsd1CameraErrCount',
      width: 140,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.jimu,
    },
    {
      title: 'BSD右后相机故障',
      dataIndex: 'bsd2CameraErrCount',
      width: 140,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.jimu,
    },
    {
      title: 'BSD后相机故障',
      dataIndex: 'bsd3CameraErrCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.jimu,
    },
    {
      title: '前向毫米雷达波故障',
      dataIndex: 'radarErrCount',
      width: 140,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.jimu,
    },
    {
      title: 'GPS设备故障',
      dataIndex: 'gpsErrCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.jimu,
    },
    {
      title: '拉线电机故障',
      dataIndex: 'cableMotorErrCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.jimu,
    },
    {
      title: '小屏故障',
      dataIndex: 'screenErrCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.jimu,
    },
    {
      title: '4G设备故障',
      dataIndex: 't4gErrCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.jimu,
    },
    {
      title: 'AEB关闭',
      dataIndex: 'aebCloseCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.jimu,
    },
    {
      title: 'AEB功能故障',
      dataIndex: 'aebErrCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.jimu,
    },
    {
      title: '7寸屏故障',
      dataIndex: 'screen7ErrCount',
      width: 120,
      deviceTypeFlag: true,
      deviceType: DeviceTypeEnum.jimu,
    },

  ];


  //仅在TLY4.0时展示前像制动字段
  // if (isTly40){
  //   columns.push(
  //     // {
  //     //   title: "前向制动报警数",
  //     //   dataIndex: "aebCount",
  //     //   width: 130,
  //     //   render: (value: number) => value===null ? '--' : value
  //     // }
  //   )
  // }

  columns.forEach((col: any) => {
    if (!col?.render) {
      col.render = (value: number) => value === null ? '--' : value;
    }
  })
  const _filterByDeviceType = (col: any) => {
    if (!col.deviceTypeFlag) {
      return true;
    }

    // TLY5C1 和 junchi1 等价处理
    const normalizedDeviceType = deviceType === DeviceTypeEnum.TLY5C1 ? DeviceTypeEnum.junchi1 : deviceType;

    // 支持 deviceType 为数组的情况
    if (Array.isArray(col.deviceType)) {
      return col.deviceType.map(String).some((type: string) => {
        // 如果列配置包含 junchi1，且当前设备是 TLY5C1，也显示
        if (type === String(DeviceTypeEnum.junchi1) && deviceType === DeviceTypeEnum.TLY5C1) {
          return true;
        }
        return type === String(deviceType);
      });
    }

    // 单个 deviceType 的情况
    if (`${col.deviceType}` === String(DeviceTypeEnum.junchi1) && deviceType === DeviceTypeEnum.TLY5C1) {
      return true;
    }
    return `${col.deviceType}` === `${deviceType}`;
  }
  const _memoCol = useMemo(() => columns.filter(_filterByDeviceType), [columns, deviceType]);
  return (
    <Fragment>
      <Tabs>
        <Tabs.TabPane tab="ADAS数据" key="operation">
          <div style={{ margin: '12px 0', textAlign: 'right' }}>
            <ExportButton
              type='primary'
              disabled={!diagnosisSearchParams}
              url={carAdasRequestPath}
              fileName={`${diagnosisSearchParams?.plateNumber}车辆分析`}
              getParamsFunc={getExportParams}
              requestMethod="GET"
              permissionKey='/car-admin/car-diagnosis/car-operation-export'
            />
          </div>
          <CommonTable
            rowKey="id"
            columns={_memoCol}
            dataSource={data}
            pagination={{
              total: totalCount,
              current: page,
              pageSize: limit,
              onChange: onPageChange,
            }}
            bordered
            loading={loading}
            scroll={{ x: '100%', y: 400 }}
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab='ADAS异常' key="errorerror">
          <JtDeviceErrorTable
            diagnosisSearchParams={diagnosisSearchParams}
            deviceType={deviceType || void 0}
            getExportParams={getExportParams}
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab='ADAS版本变更' key="change">
          <JtDeviceVersionChangeTable
            carId={carId}
            diagnosisSearchParams={diagnosisSearchParams}
            getExportParams={getExportParams}
          />
        </Tabs.TabPane>
        {(deviceType === DeviceTypeEnum.junchi1 || deviceType === DeviceTypeEnum.jimu || deviceType === DeviceTypeEnum.TLY5C1) && <Tabs.TabPane tab='ADAS参数变更' key="paramsChange">
          <JtDeviceParamsChangeTable
            carId={carId}
            deviceType={deviceType}
            diagnosisSearchParams={diagnosisSearchParams}
            getExportParams={getExportParams}
          />
        </Tabs.TabPane>}
      </Tabs>

    </Fragment>
  )
}

export default CarAdasTable;